import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { BarChart3, Clock3, Heart, RefreshCw, TrendingUp, Users } from 'lucide-react';
import { Doughnut, Line } from 'react-chartjs-2';
import { ArcElement, CategoryScale, Chart as ChartJS, Filler, Legend, LinearScale, LineElement, PointElement, Tooltip } from 'chart.js';
import axiosInstance from '../api/AxiosInstance';
import { endpoints } from '../services/endpoints';
import { showErrorToast } from '../helpers/SweetAlert';
import './AnalyticsManagement.css';

ChartJS.register(CategoryScale, LinearScale, LineElement, PointElement, ArcElement, Tooltip, Legend, Filler);

const number = (value) => new Intl.NumberFormat('en-US').format(value || 0);
const changeText = (value) => `${value >= 0 ? '+' : ''}${value || 0}% from last period`;

const AnalyticsManagement = () => {
    const [data, setData] = useState(null);
    const [days, setDays] = useState(30);
    const [loading, setLoading] = useState(true);

    const loadAnalytics = useCallback(async () => {
        setLoading(true);
        try {
            const response = await axiosInstance.get(endpoints.analyticsManagement, { params: { days } });
            setData(response.data.data);
        } catch (error) {
            showErrorToast(error.response?.data?.message || 'Unable to load analytics');
        } finally {
            setLoading(false);
        }
    }, [days]);

    useEffect(() => {
        loadAnalytics();
    }, [loadAnalytics]);

    const growthChart = useMemo(() => ({
        labels: data?.userGrowth.series.map((item) => new Date(item.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })) || [],
        datasets: [{
            label: 'New Users',
            data: data?.userGrowth.series.map((item) => item.count) || [],
            borderColor: '#202b3c',
            backgroundColor: 'rgba(32, 43, 60, .10)',
            fill: true,
            tension: .35,
            pointRadius: 2,
        }],
    }), [data]);

    const engagementChart = useMemo(() => ({
        labels: data?.engagementByContentType.map((item) => item.label) || [],
        datasets: [{
            data: data?.engagementByContentType.map((item) => item.value) || [],
            backgroundColor: ['#202b3c', '#526075', '#9aa5b4', '#d5dae1'],
            borderWidth: 0,
        }],
    }), [data]);

    if (loading && !data) return <div className="analytics-loading">Loading analytics...</div>;
    if (!data) return null;

    const summaryCards = [
        { title: 'User Retention Rate', value: `${data.summary.retentionRate}%`, change: data.summary.retentionChange, icon: Users },
        { title: 'Avg Session Duration', value: data.summary.averageSessionDuration, change: data.summary.sessionChange, icon: Clock3 },
        { title: 'Daily Active Users', value: number(data.summary.dailyActiveUsers), change: data.summary.activeUsersChange, icon: BarChart3 },
        { title: 'Engagement Rate', value: `${data.summary.engagementRate}%`, change: data.summary.engagementChange, icon: Heart },
    ];

    return (
        <section className="analytics-page">
            <div className="analytics-header">
                <div>
                    <h1>Analytics Management</h1>
                    <p>Monitor platform growth, engagement, and user behavior.</p>
                </div>
                <div className="analytics-header-actions">
                    <select value={days} onChange={(event) => setDays(Number(event.target.value))}>
                        <option value="7">Last 7 Days</option>
                        <option value="30">Last 30 Days</option>
                        <option value="90">Last 90 Days</option>
                    </select>
                    <button onClick={loadAnalytics}><RefreshCw className={loading ? 'spin' : ''} /> Refresh Data</button>
                </div>
            </div>

            <div className="analytics-summary">
                {summaryCards.map(({ title, value, change, icon: Icon }) => (
                    <article className="analytics-card analytics-metric" key={title}>
                        <div><span>{title}</span><strong>{value}</strong><small className={change < 0 ? 'negative' : ''}>{changeText(change)}</small></div>
                        <Icon />
                    </article>
                ))}
            </div>

            <div className="analytics-primary-grid">
                <article className="analytics-card analytics-chart-card">
                    <div className="analytics-card-title"><h2>User Growth Trends</h2><span>Last {days} Days</span></div>
                    <div className="analytics-chart"><Line data={growthChart} options={{ responsive: true, maintainAspectRatio: false, plugins: { legend: { display: false } }, scales: { y: { beginAtZero: true, grid: { color: '#edf0f3' } }, x: { grid: { display: false } } } }} /></div>
                    <div className="growth-totals">
                        <div><span>New Users</span><strong>{number(data.userGrowth.newUsers)}</strong></div>
                        <div><span>Returning Users</span><strong>{number(data.userGrowth.returningUsers)}</strong></div>
                        <div><span>Total Growth</span><strong>{data.userGrowth.totalGrowth >= 0 ? '+' : ''}{data.userGrowth.totalGrowth}%</strong></div>
                    </div>
                </article>

                <article className="analytics-card analytics-chart-card">
                    <div className="analytics-card-title"><h2>Engagement by Content Type</h2><span>Last {days} Days</span></div>
                    <div className="analytics-chart analytics-doughnut"><Doughnut data={engagementChart} options={{ responsive: true, maintainAspectRatio: false, cutout: '64%', plugins: { legend: { display: false } } }} /></div>
                    <div className="engagement-legend">
                        {data.engagementByContentType.map((item, index) => <div key={item.label}><span><i style={{ background: ['#202b3c', '#526075', '#9aa5b4', '#d5dae1'][index] }} />{item.label}</span><strong>{item.percentage}%</strong></div>)}
                    </div>
                </article>
            </div>

            <div className="analytics-secondary-grid">
                <article className="analytics-card">
                    <h2>Most Active Times</h2>
                    <div className="activity-heatmap">
                        {data.activeTimes.heatmap.map((item) => {
                            const max = Math.max(...data.activeTimes.heatmap.map((entry) => entry.count), 1);
                            return <span key={item.hour} title={`${item.hour}:00 - ${item.count} opens`} style={{ opacity: .15 + (item.count / max) * .85 }} />;
                        })}
                    </div>
                    <div className="active-time-details"><div><span>Peak Hour</span><strong>{data.activeTimes.peakHour}</strong></div><div><span>Peak Day</span><strong>{data.activeTimes.peakDay}</strong></div></div>
                </article>

                <article className="analytics-card">
                    <h2>Popular Sports</h2>
                    <div className="popular-sports">
                        {data.popularSports.map((sport) => <div key={sport.name}><span>{sport.name}</span><div><i><b style={{ width: `${sport.percentage}%` }} /></i><strong>{sport.percentage}%</strong></div></div>)}
                        {!data.popularSports.length && <p className="analytics-empty">No onboarding sports data yet.</p>}
                    </div>
                </article>

                <article className="analytics-card">
                    <h2>Real-time Activity</h2>
                    <div className="realtime-list">
                        {[
                            ['Posts Today', data.realtimeActivity.postsToday, 'Updated now'],
                            ['Likes Today', data.realtimeActivity.likesToday, 'Updated now'],
                            ['Messages Today', data.realtimeActivity.messagesToday, 'Updated now'],
                            ['Online Users', data.realtimeActivity.onlineUsers, 'Live count'],
                        ].map(([label, value, note]) => <div key={label}><span><b>{label}</b><small>{note}</small></span><strong>{number(value)}</strong></div>)}
                    </div>
                </article>
            </div>

            <article className="analytics-card analytics-reports">
                <div className="analytics-card-title"><h2>Detailed Analytics Reports</h2><button onClick={loadAnalytics}><RefreshCw /> Refresh Data</button></div>
                <div className="analytics-table-scroll">
                    <table><thead><tr><th>Metric</th><th>Current Value</th><th>Previous Period</th><th>Change</th><th>Trend</th><th>Target</th></tr></thead>
                        <tbody>{data.reports.map((report) => <tr key={report.metric}><td>{report.metric}</td><td>{report.currentValue}</td><td>{report.previousValue}</td><td className={report.change < 0 ? 'negative' : ''}>{report.change >= 0 ? '+' : ''}{report.change}%</td><td><TrendingUp className={report.change < 0 ? 'trend-down' : ''} /></td><td>{report.target}</td></tr>)}</tbody>
                    </table>
                </div>
            </article>
        </section>
    );
};

export default AnalyticsManagement;
